// StudentPortal — magic-link login for LaSalle Chew Flats tenants (and any
// tenant who used a personal email). Three states: email → code → dashboard.
// Session token lives in localStorage; expires in 24h.
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { EnvelopeSimple, LockKey, CheckCircle, Clock, Warning, PaperPlaneTilt, Wrench, ArrowRight, SignOut, Plus } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "field_radar_student_session";

export default function StudentPortal() {
  const [state, setState] = useState("email");     // "email" | "code" | "dash"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [session, setSession] = useState(localStorage.getItem(SESSION_KEY) || "");
  const [isLasalle, setIsLasalle] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tickets, setTickets] = useState([]);
  // Reference-code shortcut — trade `CTOEFG` for the ticket's status page.
  const [refCode, setRefCode] = useState("");
  const [refBusy, setRefBusy] = useState(false);
  const [refError, setRefError] = useState("");

  async function lookupByRef(e) {
    e?.preventDefault();
    setRefError("");
    const val = refCode.trim().toUpperCase();
    if (val.length < 4) { setRefError("Enter your full code (usually 6–8 letters)"); return; }
    setRefBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/public/chew/hub/lookup-by-ref`, { ref: val });
      window.location.assign(r.data.status_url);
    } catch (e) {
      setRefError(e.response?.data?.detail || "Could not find that code");
    } finally { setRefBusy(false); }
  }

  useEffect(() => {
    if (session) {
      loadTickets(session).then(ok => { if (ok) setState("dash"); });
    }
  }, []); // eslint-disable-line

  async function loadTickets(tok) {
    try {
      const r = await axios.get(`${BASE}/api/public/student/tickets`, {
        headers: { "X-Student-Session": tok },
      });
      setTickets(r.data.tickets || []);
      setIsLasalle(!!r.data.is_lasalle);
      return true;
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(SESSION_KEY);
        setSession("");
        setState("email");
      }
      return false;
    }
  }

  async function requestCode(e) {
    e?.preventDefault();
    setError("");
    if (!email.includes("@")) { setError("Enter a valid email"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/student/login-request`, { email: email.trim().toLowerCase() });
      setState("code");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send code. Try again.");
    } finally { setBusy(false); }
  }

  async function verifyCode(e) {
    e?.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(code)) { setError("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/public/student/login-verify`, {
        email: email.trim().toLowerCase(),
        code,
      });
      localStorage.setItem(SESSION_KEY, r.data.session_token);
      setSession(r.data.session_token);
      setIsLasalle(!!r.data.is_lasalle);
      await loadTickets(r.data.session_token);
      setState("dash");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired code");
    } finally { setBusy(false); }
  }

  async function logout() {
    if (session) {
      try { await axios.post(`${BASE}/api/public/student/logout`, {}, {
        headers: { "X-Student-Session": session },
      }); } catch (e) { /* ignore */ }
    }
    localStorage.removeItem(SESSION_KEY);
    setSession(""); setEmail(""); setCode(""); setTickets([]);
    setState("email");
  }

  // ── UI states ─────────────────────────────────────────────────────

  const brand = isLasalle || (email.trim().toLowerCase().endsWith("@lasalle.edu")) ? "#0a2461" : "#00C4CC";

  if (state === "dash") {
    return <Dashboard tickets={tickets} isLasalle={isLasalle} onLogout={logout} onReload={()=>loadTickets(session)}/>;
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="student-portal">
      <div style={{ background: brand }} className="text-white px-4 py-6">
        <div className="max-w-md mx-auto">
          <div className="text-[10px] font-black uppercase tracking-[2px] opacity-90">Field Radar Tenant Portal</div>
          <div className="text-2xl font-black leading-tight mt-1">{isLasalle || state === "code" ? "Welcome Back" : "My Requests"}</div>
          <div className="text-xs opacity-80 mt-1">Check your maintenance status · reply to the office</div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-6 pb-32">
        {error && (
          <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 flex gap-2" data-testid="student-error">
            <Warning size={16}/> {error}
          </div>
        )}

        {state === "email" && (
          <form onSubmit={requestCode} className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-black">Sign in to see your requests</h2>
              <p className="text-sm text-slate-600 mt-1">
                Enter the email you used when submitting a maintenance request. We'll send you a one-time code — no password to remember.
              </p>
            </div>
            <label className="block">
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Email</div>
              <div className="relative">
                <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                  placeholder="you@lasalle.edu"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-3"
                  data-testid="student-email-input" autoComplete="email"/>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                🔒 We hash your email so the office never sees the actual address — only "email on file".
              </div>
            </label>
            <button type="submit" disabled={busy}
              className="w-full rounded-xl font-black py-4 text-sm text-white disabled:opacity-50"
              style={{ background: brand }}
              data-testid="student-send-code-btn">
              {busy ? "Sending…" : "Email me a code →"}
            </button>
            <Link to="/report/chew-flats" className="block text-center text-xs font-bold text-slate-500 hover:text-slate-900 underline" data-testid="student-new-request-link">
              First time? Submit a maintenance request →
            </Link>
          </form>
        )}

        {/* ── OR — Reference-code shortcut ─────────────────────── */}
        {state === "email" && (
          <div className="mt-4 bg-white rounded-2xl p-5 shadow-sm space-y-3" data-testid="student-ref-code-card">
            <div className="flex items-center gap-2">
              <div className="h-6 flex-1 border-t border-slate-200"/>
              <div className="text-[10px] font-black uppercase tracking-[2px] text-slate-400">Or</div>
              <div className="h-6 flex-1 border-t border-slate-200"/>
            </div>
            <div>
              <h2 className="text-sm font-black">Have a reference code?</h2>
              <p className="text-xs text-slate-600 mt-1">
                We texted or emailed you a code like <code className="bg-slate-100 px-1 py-0.5 rounded font-mono text-[11px]">CTOEFG</code> when you filed the request. Enter it below to jump straight to that ticket — no email needed.
              </p>
            </div>
            {refError && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2" data-testid="student-ref-error">
                {refError}
              </div>
            )}
            <form onSubmit={lookupByRef} className="space-y-3">
              <input value={refCode} onChange={e => setRefCode(e.target.value.toUpperCase())}
                placeholder="CTOEFG"
                inputMode="text" autoCapitalize="characters" autoComplete="off" maxLength={12}
                data-testid="student-ref-input"
                className="w-full border border-slate-300 rounded-lg px-3 py-3 font-mono text-lg tracking-widest uppercase text-center"/>
              <button type="submit" disabled={refBusy || refCode.trim().length < 4}
                data-testid="student-ref-lookup-btn"
                className="w-full rounded-xl font-black py-3 text-xs uppercase tracking-widest text-white disabled:opacity-50"
                style={{ background: brand }}>
                {refBusy ? "Looking up…" : "Check status with this code →"}
              </button>
            </form>
          </div>
        )}

        {state === "code" && (
          <form onSubmit={verifyCode} className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-black">Check your inbox</h2>
              <p className="text-sm text-slate-600 mt-1">
                We just emailed a 6-digit code to <strong className="text-slate-900">{email}</strong>. It expires in 10 minutes.
              </p>
            </div>
            <label className="block">
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Code</div>
              <div className="relative">
                <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="123456"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-3 font-mono text-lg tracking-widest text-center"
                  data-testid="student-code-input" autoFocus/>
              </div>
            </label>
            <button type="submit" disabled={busy || code.length !== 6}
              className="w-full rounded-xl font-black py-4 text-sm text-white disabled:opacity-40"
              style={{ background: brand }}
              data-testid="student-verify-code-btn">
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <button type="button" onClick={()=>{setState("email"); setError(""); setCode("");}}
              className="w-full text-xs font-bold text-slate-500 hover:text-slate-900 underline" data-testid="student-back-btn">
              ← Wrong email? Go back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────
function Dashboard({ tickets, isLasalle, onLogout, onReload }) {
  const brand = isLasalle ? "#0a2461" : "#00C4CC";
  const open = tickets.filter(t => !["done","closed","completed"].includes(t.status));
  const closed = tickets.filter(t => ["done","closed","completed"].includes(t.status));

  return (
    <div className="min-h-screen bg-slate-100" data-testid="student-dashboard">
      <div style={{ background: brand }} className="text-white px-4 py-6">
        <div className="max-w-md mx-auto flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[2px] opacity-90">My Maintenance Requests</div>
            <div className="text-2xl font-black leading-tight mt-1">{isLasalle ? "LaSalle Portal" : "Field Radar"}</div>
            <div className="text-xs opacity-80 mt-1">{tickets.length} total · {open.length} open</div>
          </div>
          <button onClick={onLogout} className="text-white/80 hover:text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1" data-testid="student-logout-btn">
            <SignOut size={12}/> Sign out
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4 pb-32">
        {/* New request shortcut */}
        <Link to="/report/chew-flats"
          className="flex items-center justify-between gap-3 rounded-2xl p-4 shadow-sm mb-4 text-white font-black"
          style={{ background: `linear-gradient(135deg, ${brand}, ${brand}dd)` }}
          data-testid="student-new-request-cta">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80">Need something fixed?</div>
            <div className="text-lg mt-0.5">Submit a new request</div>
          </div>
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Plus weight="bold" size={20}/>
          </div>
        </Link>

        {!tickets.length && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm" data-testid="student-empty">
            <div className="text-4xl mb-3">🛠️</div>
            <div className="font-black text-lg">No requests yet</div>
            <div className="text-sm text-slate-500 mt-1">When you submit a maintenance request, it'll show here.</div>
          </div>
        )}

        {/* Open tickets */}
        {open.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2 px-1">OPEN · {open.length}</div>
            <div className="space-y-2">
              {open.map(t => <TicketCard key={t.id} t={t}/>)}
            </div>
          </div>
        )}

        {/* Closed tickets */}
        {closed.length > 0 && (
          <div>
            <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2 px-1">CLOSED · {closed.length}</div>
            <div className="space-y-2 opacity-70">
              {closed.map(t => <TicketCard key={t.id} t={t}/>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TicketCard({ t }) {
  const prioColor = { urgent: "bg-red-500", high: "bg-orange-500", normal: "bg-cyan-500" }[t.priority] || "bg-slate-400";
  const statusColor = {
    submitted: "bg-orange-100 text-orange-800",
    scheduled: "bg-emerald-100 text-emerald-800",
    in_progress: "bg-sky-100 text-sky-800",
    done: "bg-slate-100 text-slate-600",
    draft: "bg-slate-100 text-slate-500",
  }[t.status] || "bg-slate-100 text-slate-600";
  const adminMsgCount = (t.messages || []).filter(m => m.from === "admin").length;
  const url = `/report/chew-flats/status/${t.id}?ref=${t.ref_code}`;

  return (
    <a href={url} className="block bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition" data-testid={`student-ticket-${t.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-1 rounded-full self-stretch ${prioColor}`}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${statusColor}`}>{(t.status || "open").replace("_"," ")}</span>
            <span className="text-[10px] font-bold text-slate-500">Unit {t.unit}</span>
            <span className="ml-auto text-[10px] font-mono text-slate-400">{t.ref_code}</span>
          </div>
          <div className="font-black text-slate-900 mt-1 truncate">{t.subject || "Maintenance request"}</div>
          {t.description && (
            <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
            {t.accepted_window && (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                <Clock size={12}/> Scheduled {t.accepted_window.start}
              </span>
            )}
            {t.photos?.length ? <span>{t.photos.length}📷</span> : null}
            {adminMsgCount > 0 && (
              <span className="inline-flex items-center gap-1 text-cyan-700 font-bold">
                <PaperPlaneTilt size={12}/> {adminMsgCount} reply{adminMsgCount === 1 ? "" : "s"}
              </span>
            )}
            {t.wo_id && (
              <span className="inline-flex items-center gap-1 text-slate-500">
                <Wrench size={12}/> WO placed
              </span>
            )}
          </div>
        </div>
        <ArrowRight size={16} className="text-slate-300 self-center"/>
      </div>
    </a>
  );
}
